<template>
  <div class="app-search-select" :class="{searchmode: !suggest}" v-if="exoSearch">
    <div v-if="suggest"><h2 class="search-headline"><slot name="lblHeadline">Wie kann ich dir helfen?</slot></h2></div>
    <b-form  @submit="performSearch">
      <b-form-group class="custom-input-select">
        <div class="dropdown-wrapper" @scroll="grabScrollEvent">
          <div class="dropdown" ref="dropdown">
            <div class="clear-input" v-if="showClear" @click.prevent="clearSelection"></div>
            <div class="dropdown-border">
              <input
                id="searchSelect"
                ref="input"
                class="form-control"
                :class="[open ? 'option-list-active' : '', showClear ? 'clearable' : '']"
                type="text"
                name="searchSelect"
                spellcheck="false"
                :value="userInput"
                :placeholder="placeholder"
                @input="performUserInput"
                @keyup.enter.prevent="onSelectByReturnKey();"
                @keyup.tab="onEnterByTab();"
                @keydown.tab="onBlurByTab();"
                @focus="onFocusSelectField()"
                @click="onFocusSelectField()"
                @keydown="onArrowKeyScroll"
                autocomplete="off"
              >
            </div>
            <div class="option-list-wrapper"
                @mousemove="scrollHandlerOnDrag"
                @drag="scrollHandlerOnDrag"
                @mouseover="scrollHandlerOnDragCatch"
                @mouseup="scrollHandlerOnDragEnd"
                @mouseleave="scrollHandlerOnDragEnd"
                v-show="open">
              <ul ref="dropdownListContainer" tabindex="-1" class="option-list" @scroll="scrollHandlerOnScroll">
                <li class="default-items" v-if="showDefaultOptions"><strong><slot name="lblMostSearched">Meist gesucht</slot></strong></li>
                <li v-for="(option, index) in availableOptions"
                    :key="index"
                    :class="{ focus: index === focusIndex }"
                    @mouseover.prevent="listItemMouseOverAndMouseLeave(index)"
                    @mouseleave.prevent="listItemMouseOverAndMouseLeave(index)"
                    @click="itemSelection(option)"
                >
                  <div class="item-wrapper">
                    <a :href="option.url" @click="handleEventTracking(index, $event)">
                      <span class="icon"><img :src="option.image" /></span>
                      <span class="text" v-html="option.title"></span>
                    </a>
                  </div>
                </li>
                <li class="all-results" v-if="!showDefaultOptions">
                  <button class="filled yellow" aria-label="Suche"><span class="button-text">{{ resultCount }} <slot name="lblAllResults">Ergebnisse anschauen</slot></span></button>
                </li>
              </ul>
              <div ref="scrollHandle" class="scroll-handle" :class="{'invisible': !showScrollbar}" @mousedown="scrollHandlerOnDragStart"></div>
            </div>
          </div>
        </div>
      </b-form-group>
    </b-form>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import beErrors from '@/mixins/beErrors'
import useFeature from '@/mixins/useFeature'

export default {
  name: 'SearchSelect',
  data () {
    return {
      userInput: '',
      open: false,
      focusIndex: null,
      lastFocused: null,
      scrollHandlerDragging: false,
      scrollHandlerDragStartPos: 0,
      scrollHandlerDragLastPageY: 0,
      options: [],
      resultCount: 0,
      selected: null,
      debounceTimer: null
    }
  },
  created () {
    if (this.exoSearch) {
      document.addEventListener('click', this.documentClick)
      document.addEventListener('focusout', this.focusChanged)
    }
  },
  mounted () {
    if (!this.exoSearch) {
      return
    }
    const urlParams = new URLSearchParams(window.location.search)
    const searchParam = urlParams.get('q')
    const _this = this
    if (!this.suggest && ((this.searchPhrase && this.searchPhrase.length >= 3) || (searchParam && searchParam.length >= 3))) {
      this.userInput = this.searchPhrase ? this.searchPhrase : searchParam
      if (!this.searchPhrase) {
        this.setSearchPhrase(this.userInput)
      }
      this.querySearch({ searchQuery: this.userInput, start: 0, rows: 6 }).then((result) => {
        if (!result) {
          const message = _this.beErrorGetLastMessage()
          _this.errorToast(message)
          const data = {
            type: 'technical',
            message: message
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        }
      }).catch(() => {
        const message = _this.beErrorGetLastMessage()
        _this.errorToast(message)
        const data = {
          type: 'technical',
          message: message
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('error', data)
        }
      })
    }
  },
  watch: { },
  computed: {
    ...mapGetters('search', [
      'searchPhrase'
    ]),
    showScrollbar: function () {
      if (!this.open) {
        return false
      }
      return true
    },
    showDefaultOptions: function () {
      return (this.userInput === null || this.options.length === 0)
    },
    availableOptions: function () {
      if (this.showDefaultOptions) {
        return JSON.parse(this.defaultOptions)
      }
      return this.options
    },
    indexForSelection: function () {
      const comp = this
      if (comp.selected === null) {
        return 0
      }
      const index = comp.selected.key
      if (index < 0) return 0
      return index
    },
    showClear: function () {
      if (!this.open) {
        return false
      }
      return this.userInput !== null && this.userInput.length >= 3
    }
  },
  methods: {
    ...mapMutations('search', [
      'setSearchPhrase',
      'resetState'
    ]),
    ...mapActions('search', [
      'querySearchSuggestions',
      'querySearch'
    ]),
    // Event Hanlding
    onEnterByTab () {
      this.onFocusSelectField()
      this.findSelected()
    },
    onFocusSelectField () {
      this.open = true
      if (this.userInput && this.userInput.length >= 3) {
        this.loadSuggestions()
      }
    },
    itemSelection (option) {
      this.applySelection(option)
    },
    applySelection (option) {
      this.selected = option
      this.open = false
      this.focusIndex = null
    },
    listItemMouseOverAndMouseLeave (index) {
      if (!this.scrollHandlerDragging) {
        this.focusIndex = index
      }
    },
    scrollHandlerOnDragCatch (e) {
      if (this.scrollHandlerDragging) {
        e.preventDefault()
      }
    },
    scrollHandlerOnDrag (e) {
      e.preventDefault()
      if (this.scrollHandlerDragging) {
        const ch = this.$refs.dropdownListContainer.clientHeight
        const sh = this.$refs.dropdownListContainer.scrollHeight
        const maxSt = sh - ch
        const delta = e.pageY - this.scrollHandlerDragLastPageY
        this.scrollHandlerDragLastPageY = e.pageY
        let cp = (this.scrollHandlerDragStartPos += delta)
        // boundaries
        if (cp < 10) {
          cp = 10
        }
        if (cp > ch - 30) {
          cp = ch - 30
        }
        const relativePositionOfHandle = (cp - 10) / (ch - 40)
        const raf = window.requestAnimationFrame || window.setImmediate || function (c) { return setTimeout(c, 0) }
        // scrollTop must be between [0, maxSt] ~
        raf(() => {
          this.$refs.dropdownListContainer.scrollTop = maxSt * relativePositionOfHandle
          this.$refs.scrollHandle.style.top = cp + 'px'
        })
      }
    },
    scrollHandlerOnDragStart (e) {
      e.preventDefault()
      this.scrollHandlerDragging = true
      this.scrollHandlerDragStartPos = this.$refs.scrollHandle.offsetTop
      this.scrollHandlerDragLastPageY = e.pageY
    },
    scrollHandlerOnDragEnd (e) {
      this.scrollHandlerDragging = false
    },
    scrollHandlerOnScroll (e) {
      if (!this.scrollHandlerDragging && this.$refs.dropdownListContainer) {
        const ch = this.$refs.dropdownListContainer.clientHeight
        const st = this.$refs.dropdownListContainer.scrollTop
        const sh = this.$refs.dropdownListContainer.scrollHeight
        const sr = ch / sh
        const maxSt = sh - ch
        const raf = window.requestAnimationFrame || window.setImmediate || function (c) { return setTimeout(c, 0) }

        // scrollable
        if (sr < 1) {
          // calculate top position of scroll handler within list
          let ht = Math.floor(st / maxSt * ch)
          if (ht < 10) {
            ht = 10
          }
          if (ht > ch - 30) {
            ht = ch - 30
          }
          raf(() => {
            if (this.$refs.scrollHandle) {
              this.$refs.scrollHandle.style.display = 'block'
              this.$refs.scrollHandle.style.top = ht + 'px'
            }
          })
        } else {
          raf(() => {
            // nothing to scroll
            if (this.$refs.scrollHandle) {
              this.$refs.scrollHandle.style.display = 'none'
            }
          })
        }
      }
    },
    scrollWhenSelected () {
      const comp = this
      this.$nextTick(function () {
        if (comp.$el.querySelector('.focus') !== null && comp.$el.querySelector('.option-list') !== null) {
          comp.$el.querySelector('.option-list').scrollTop = comp.$el.querySelector('.focus').offsetTop
        }
      })
    },
    findSelected () {
      this.focusIndex = this.indexForSelection
      if (this.indexForSelection > 0) {
        this.scrollWhenSelected()
      }
    },
    documentClick (e) {
      if (this.open) {
        const el = this.$refs.dropdown
        if (el && el !== e.target && !el.contains(e.target) && e.target.id !== 'search-clear-input') {
          this.applySelection(this.selected)
        }
      }
    },
    grabScrollEvent (e) {
      if (e.stopPropagation) {
        e.stopPropagation()
      }
    },
    clearSelection () {
      // only if we are in search mode
      if (this.open) {
        this.reset()
      }
    },
    performSearch (event) {
      event.preventDefault()
      this.setSearchPhrase(this.userInput)
      const _this = this
      // Goto search page if in suggest mode
      if (this.suggest) {
        location.href = this.searchLink
      } else {
        this.open = false
        this.querySearch({ searchQuery: this.userInput, start: 0, rows: 6 }).then((result) => {
          if (!result) {
            const message = _this.beErrorGetLastMessage()
            _this.errorToast(message)
            const data = {
              type: 'technical',
              message: message
            }
            if (typeof window.trackingNS?.track?.tracking === 'function') {
              window.trackingNS.track.tracking('error', data)
            }
          }
        }).catch(() => {
          const message = _this.beErrorGetLastMessage()
          _this.errorToast(message)
          const data = {
            type: 'technical',
            message: message
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        })
      }
    },
    reset () {
      this.selected = null
      this.userInput = null
      this.options = []
      this.resetState()
    },
    focusChanged (event) {
      if (event) {
        const el = this.$refs.dropdown
        if (el !== undefined && el !== event.target && !el.contains(event.target)) {
        } else {
          if ((navigator.userAgent.indexOf('MSIE') !== -1) || (!!document.documentMode === true)) {
            if (document.getElementById('nationality-select-input-field')) {
              document.getElementById('nationality-select-input-field').tabIndex = 0
            }
          } else {
            this.$nextTick(function () {
              if (document.getElementById('nationality-select-input-field')) {
                document.getElementById('nationality-select-input-field').tabIndex = 0
              }
            })
          }
        }
      }
    },
    performUserInput (event) {
      this.open = true
      this.userInput = event.target.value
      if (this.userInput && this.userInput.length >= 3) {
        this.debounce(() => this.loadSuggestions())()
      } else {
        this.options = []
      }
    },
    loadSuggestions () {
      const _this = this
      this.querySearchSuggestions({ suggestQueryString: this.userInput }).then((options) => {
        if (options) {
          this.options = options.length > 4 ? options.slice(0, 4) : options
          this.resultCount = options.length > 0 ? options[0].info : 0
        } else {
          const message = _this.beErrorGetLastMessage()
          _this.errorToast(message)
          const data = {
            type: 'technical',
            message: message
          }
          if (typeof window.trackingNS?.track?.tracking === 'function') {
            window.trackingNS.track.tracking('error', data)
          }
        }
      }).catch(() => {
        const message = _this.beErrorGetLastMessage()
        _this.errorToast(message)
        const data = {
          type: 'technical',
          message: message
        }
        if (typeof window.trackingNS?.track?.tracking === 'function') {
          window.trackingNS.track.tracking('error', data)
        }
      })
    },
    // key handling
    onArrowKeyScroll (event) {
      let elHeight
      let y = 0
      const numEl = this.availableOptions.length
      let prevent = false
      let scrollTo = y

      if (document.querySelector('.focus')) {
        elHeight = this.$el.querySelector('.focus').offsetHeight
      } else {
        elHeight = 30
      }
      if (this.$el.querySelector('.option-list')) {
        y = this.$el.querySelector('.option-list').scrollTop
      }
      switch (event.keyCode) {
        // page up
        case 33:
          this.focusIndex = 0
          scrollTo = 0
          prevent = true
          break
        // arrow up
        case 38:
          if (this.focusIndex > 0) {
            this.focusIndex--
          }
          scrollTo = (this.focusIndex * elHeight)
          prevent = true
          break
        // page down
        case 34:
          this.focusIndex = numEl - 1
          scrollTo = (this.focusIndex * elHeight)
          prevent = true
          break
        // arrow down
        case 40:
          if (this.focusIndex < numEl - 1) {
            this.focusIndex++
          }
          scrollTo = (this.focusIndex * elHeight)
          prevent = true
          break
      }

      this.lastFocused = null
      if (this.focusIndex < this.availableOptions.length) {
        this.lastFocused = this.availableOptions[this.focusIndex]
      }

      // ensure valid range
      if (scrollTo < 0) {
        scrollTo = 0
      }

      if (scrollTo > (numEl - 1) * elHeight) {
        scrollTo = (numEl - 1) * elHeight
      }

      if (this.$el.querySelector('.option-list')) {
        this.$el.querySelector('.option-list').scrollTop = scrollTo
      }

      if (prevent) {
        event.preventDefault()
      }
    },
    onSelectByReturnKey () {
      this.itemSelection(this.lastFocused)
      this.$refs.input.blur()
    },
    onBlurByTab () {
      this.itemSelection(this.availableOptions[this.focusIndex])
    },
    stripTags (html) {
      var tmpDiv = document.createElement('div')
      tmpDiv.innerHTML = html
      return tmpDiv.textContent || tmpDiv.innerText
    },
    handleEventTracking (resultIndex, event) {
      event.preventDefault()
      const cookieData = this.searchPhrase + ':::' + (resultIndex + 1) + ':::' + this.stripTags(this.availableOptions[resultIndex].title) + ':::' + this.resultCount + ':::' + 'autosuggest'
      if (window.cookieNS && window.cookieNS.main) {
        window.cookieNS.main.setCookie('searchData', cookieData, 1)
      }
      location.href = event.currentTarget.href
    },
    debounce (func, timeout = 250) {
      return (...args) => {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(() => { func.apply(this, args) }, timeout)
      }
    }
  },
  mixins: [
    beErrors,
    useFeature('exoSearch')
  ],
  props: {
    placeholder: {
      type: String,
      default: 'Ich suche nach...'
    },
    defaultOptions: {
      type: String
    },
    searchLink: {
      type: String
    },
    suggest: {
      type: Boolean,
      default: true
    }
  }
}
</script>
